/**
 * Optional Personal Library–style commerce fields for user 3D models.
 */

import {
  Currency,
  DEFAULT_FLOORPLAN_CURRENCY,
  normalizeCurrency,
} from "../../../utils/currency";

export type UserModel3DCommerceFormState = {
  price: string;
  /** ISO currency for the entered price (defaults to project display currency). */
  currency: Currency;
  url: string;
  brand: string;
};

export type UserModel3DCommerceMutationVars = {
  price?: number | null;
  currency?: string | null;
  url?: string | null;
  brand?: string | null;
};

export const emptyUserModel3DCommerceForm = (
  defaultCurrency: string = DEFAULT_FLOORPLAN_CURRENCY,
): UserModel3DCommerceFormState => ({
  price: "",
  currency: normalizeCurrency(defaultCurrency),
  url: "",
  brand: "",
});

export const commerceFormFromModel = (
  model: {
    price?: number | null;
    currency?: string | null;
    url?: string | null;
    brand?: string | null;
  },
  fallbackCurrency: string = DEFAULT_FLOORPLAN_CURRENCY,
): UserModel3DCommerceFormState => ({
  price:
    typeof model.price === "number" && Number.isFinite(model.price)
      ? String(model.price)
      : "",
  currency: normalizeCurrency(model.currency ?? fallbackCurrency),
  url: model.url?.trim() ?? "",
  brand: model.brand?.trim() ?? "",
});

/**
 * Parse form strings for create (upload / generate). Empty fields are omitted.
 */
export const parseCommerceFormForCreate = (
  form: UserModel3DCommerceFormState,
): UserModel3DCommerceMutationVars => {
  const vars: UserModel3DCommerceMutationVars = {};
  const price = parseOptionalPrice(form.price);
  if (price !== undefined) {
    vars.price = price;
    vars.currency = normalizeCurrency(form.currency);
  }
  const url = form.url.trim();
  if (url) vars.url = url;
  const brand = form.brand.trim();
  if (brand) vars.brand = brand;
  return vars;
};

/**
 * Parse form strings for update. Empty fields clear stored values (null).
 */
export const parseCommerceFormForUpdate = (
  form: UserModel3DCommerceFormState,
): UserModel3DCommerceMutationVars => {
  const priceTrimmed = form.price.trim();
  const price =
    priceTrimmed === "" ? null : parseOptionalPrice(form.price);
  if (priceTrimmed !== "" && price === undefined) {
    throw new Error("Price must be a non-negative number");
  }
  const url = form.url.trim();
  const brand = form.brand.trim();
  return {
    price: price ?? null,
    currency: price != null ? normalizeCurrency(form.currency) : null,
    url: url || null,
    brand: brand || null,
  };
};

const parseOptionalPrice = (raw: string): number | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const normalized = trimmed.replace(",", ".");
  const value = Number(normalized);
  if (!Number.isFinite(value) || value < 0) {
    throw new Error("Price must be a non-negative number");
  }
  return value;
};

/** Stable Product List id for a placed user 3D model (not a catalog Mongo id). */
export const userModelProductListId = (modelId: string): string =>
  `user-model:${modelId}`;

export const isUserModelProductListId = (productId: string): boolean =>
  productId.startsWith("user-model:");
